/**
 * PerChildDepositCapture · iter303ak-fix23k — Accepted · T10 · Action 20
 * ─────────────────────────────────────────────────────────────────────
 * Per-child deposit capture. Replaces the single-row DepositCapture so
 * each accepted quote's deposit can be tracked independently. Rows
 * where `deposit_amount === 0` render as read-only "No deposit required"
 * (never blocking advance). Rows where deposit is required capture
 * amount / date / method / reference / note per row.
 *
 * Roll-ups written to captured_data feed:
 *   • letter_key_selector `accepted_deposit_state` (T20 A10/A30)
 *   • per_child_deposits map (letter body render)
 *   • deposit_required_total / deposit_captured_total (compat)
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Wallet, CheckCircle2, AlertCircle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const money = (n) => {
  const v = Number(n) || 0;
  return `$${v.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function PerChildDepositCapture({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const methodOptions = (params.method_options
    || 'Bank transfer,Cash,Cheque,Card,Other').split(',').map((s) => s.trim()).filter(Boolean);

  // Derive accepted children from upstream decisions.
  const acceptedChildren = useMemo(() => {
    const decisions = capturedData?.decisions
      || capturedData?.accepted_variants_pick?.decisions
      || capturedData?.await_quote_response?.decisions
      || capturedData?.appraisal?.child_decisions
      || capturedData?.appraisal_record?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  // Fetch child docs so we have deposit_amount + quote_total per child.
  const [childDocs, setChildDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState(value?.per_child_deposits ? Object.entries(value.per_child_deposits).map(([id, r]) => ({ ...r, child_id: id })) : []);
  const seededRef = useRef(rows.length > 0);

  const load = useCallback(async () => {
    if (!appraisalId) { setLoading(false); return; }
    setLoading(true); setErr('');
    try {
      const { data } = await api.get(`/appraisals/${appraisalId}/children`);
      const kids = Array.isArray(data?.children) ? data.children : [];
      const byId = {};
      for (const c of kids) byId[c.id || c._id] = c;
      // Fallback single-quote: use parent appraisal itself if no children.
      // Batch T follow-up · workflow integration (2026-02): prefer the
      // Deposit Engine value on the parent doc; fall back to legacy for BC.
      if (kids.length === 0) {
        const app = capturedData?.appraisal || capturedData?.appraisal_record || {};
        byId[appraisalId] = {
          id: appraisalId,
          appraisal_no: app.appraisal_no || null,
          quote_type_ref: app.quote_type_ref || 'This appraisal',
          variant_label: app.variant_label || '',
          quote_total: app.quote_total ?? 0,
          deposit_amount: app.deposit_engine_amount ?? app.deposit_amount ?? 0,
          deposit_engine_amount: app.deposit_engine_amount ?? null,
          deposit_engine_reason: app.deposit_engine_reason || '',
        };
      }
      setChildDocs(byId);

      // Seed rows from acceptedChildren + child doc data (once).
      if (!seededRef.current) {
        const priorFromCaptured = capturedData?.per_child_deposits || {};
        const seeded = acceptedChildren.map(c => {
          const doc = byId[c.child_id] || {};
          const prior = priorFromCaptured[c.child_id] || {};
          const requiredAmt = Number(doc.deposit_engine_amount ?? doc.deposit_amount) || 0;
          return {
            child_id: c.child_id,
            quote_type_ref: c.quote_type_ref || doc.quote_type_ref || '',
            variant_label: c.variant_label || doc.variant_label || '',
            appraisal_no: c.appraisal_no || doc.appraisal_no || null,
            required_amount: requiredAmt,
            amount: prior.amount != null ? String(prior.amount) : (requiredAmt > 0 ? String(requiredAmt) : ''),
            date: prior.date || todayLocal(),
            method: prior.method || methodOptions[0] || 'Bank transfer',
            reference: prior.reference || '',
            note: prior.note || '',
            captured: !!prior.stamped_at,
          };
        });
        setRows(seeded);
        seededRef.current = true;
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to load children');
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [appraisalId, acceptedChildren]);

  useEffect(() => { load(); }, [load]);

  const updateRow = (child_id, patch) => {
    setRows(prev => prev.map(r => r.child_id === child_id ? { ...r, ...patch } : r));
  };

  // Derived roll-ups + push to captured_data on every change.
  const derivedState = useMemo(() => {
    const per_child = {};
    let requiredTotal = 0;
    let capturedTotal = 0;
    let anyRequired = false;
    let allCaptured = true;
    for (const r of rows) {
      const isRequired = r.required_amount > 0;
      if (isRequired) {
        anyRequired = true;
        requiredTotal += r.required_amount;
        // Pre-UAT v10 · Partial Deposit fix (Option A · Strict)
        // ────────────────────────────────────────────────────
        // Previously: `Number(r.amount) > 0` — any dollar counted as
        // "captured", so a $100 partial payment on a $500 required
        // deposit routed to the "Deposit Received — thanks!" letter,
        // hiding the $400 shortfall.
        // Now: require the captured amount to meet or exceed the
        // required amount. Shortfalls stay `allCaptured=false`, which
        // routes the letter selector to `accepted_awaiting_deposit`
        // (with the shortfall shown via the {deposit_amount_owing} token).
        const amt = Number(r.amount) || 0;
        const captured = amt >= r.required_amount;
        if (captured) capturedTotal += amt;
        else if (amt > 0) capturedTotal += amt;  // partial: still count $ toward total for banner display
        if (!captured) allCaptured = false;
        per_child[r.child_id] = captured ? {
          amount: amt,
          date: r.date,
          method: r.method,
          reference: r.reference,
          note: r.note,
          required_amount: r.required_amount,
          stamped_at: r.captured ? undefined : null, // filled on advance
        } : {
          amount: amt,  // preserve partial amount so operators see what's been paid
          required_amount: r.required_amount,
          date: r.date || null,
          method: r.method || null,
          reference: r.reference || null,
          note: r.note,
          awaiting: true,
          partial: amt > 0,  // Pre-UAT v10 · flag for downstream UI hints
        };
      } else {
        per_child[r.child_id] = { amount: 0, required_amount: 0, not_required: true };
      }
    }
    return {
      per_child_deposits: per_child,
      deposit_required_total: requiredTotal,
      deposit_captured_total: capturedTotal,
      deposit_any_required: anyRequired,
      deposit_all_captured: !anyRequired ? true : allCaptured,
      // For legacy letter_key_selector compat (top-level accepted_deposit_state)
      deposit_capture_result: {
        deposit_captured: !anyRequired ? true : allCaptured,
        deposit: anyRequired && allCaptured ? {
          amount: capturedTotal,
          stamped_at: new Date().toISOString(),
        } : null,
      },
    };
  }, [rows]);

  useEffect(() => { if (onChange) onChange(derivedState); }, [derivedState, onChange]);

  const advance = async () => {
    setSaving(true); setErr('');
    try {
      if (appraisalId) {
        const nowIso = new Date().toISOString();
        const per_child_final = {};
        for (const [cid, d] of Object.entries(derivedState.per_child_deposits)) {
          if (d.not_required || d.awaiting) {
            per_child_final[cid] = d;
          } else {
            per_child_final[cid] = { ...d, stamped_at: d.stamped_at || nowIso };
          }
        }
        await api.patch(`/appraisals/${appraisalId}`, {
          per_child_deposits: per_child_final,
          deposit_required: derivedState.deposit_any_required,
          deposit_captured_at: derivedState.deposit_all_captured && derivedState.deposit_any_required ? nowIso : null,
          deposit: (derivedState.deposit_any_required && derivedState.deposit_all_captured) ? {
            amount: derivedState.deposit_captured_total,
            stamped_at: nowIso,
          } : null,
        });
      }
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const anyRequired = derivedState.deposit_any_required;
  const allCaptured = derivedState.deposit_all_captured;

  return (
    <div className="space-y-4" data-testid="wf-per-child-deposit-capture">
      <div className={`rounded-xl border p-4 ${anyRequired ? 'border-amber-200 bg-amber-50/60' : 'border-slate-200 bg-slate-50'}`}>
        <div className="flex items-start gap-3">
          <Wallet className={`w-5 h-5 mt-0.5 shrink-0 ${anyRequired ? 'text-amber-700' : 'text-slate-500'}`} />
          <div className="flex-1">
            <div className={`text-sm font-semibold ${anyRequired ? 'text-amber-900' : 'text-slate-800'}`}>
              Deposit check (per accepted quote)
            </div>
            <div className={`text-sm mt-1 ${anyRequired ? 'text-amber-800' : 'text-slate-600'}`}>
              {params.prompt || 'Capture deposit received per accepted quote. Rows without a required deposit auto-skip.'}
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="p-6 text-sm text-slate-400 italic">Loading children…</div>}

      {!loading && rows.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No accepted quotes found. Go back and confirm at least one.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-2">
          {rows.map((r) => {
            const isRequired = r.required_amount > 0;
            const label = [r.quote_type_ref, r.variant_label].filter(Boolean).join(' · ');
            const partial = isRequired && Number(r.amount) > 0 && Number(r.amount) < r.required_amount;
            return (
              <div key={r.child_id} className={`p-3 border rounded-lg ${isRequired ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`wf-per-child-deposit-row-${r.child_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-sm font-medium text-slate-800 flex items-center gap-2 flex-1">
                    {label || 'Quote'}
                    {r.appraisal_no && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                        A{r.appraisal_no}
                      </span>
                    )}
                    {!isRequired && (
                      <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                        No deposit required
                      </span>
                    )}
                    {isRequired && (
                      <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        Required: {money(r.required_amount)}
                      </span>
                    )}
                  </div>
                </div>
                {isRequired && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Amount received</label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={r.amount}
                          onChange={(e) => updateRow(r.child_id, { amount: e.target.value })}
                          className="w-full pl-5 pr-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                          data-testid={`wf-per-child-deposit-amount-${r.child_id}`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Date received</label>
                      <input
                        type="date"
                        value={r.date}
                        onChange={(e) => updateRow(r.child_id, { date: e.target.value })}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                        data-testid={`wf-per-child-deposit-date-${r.child_id}`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Method</label>
                      <select
                        value={r.method}
                        onChange={(e) => updateRow(r.child_id, { method: e.target.value })}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-amber-500"
                        data-testid={`wf-per-child-deposit-method-${r.child_id}`}
                      >
                        {methodOptions.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Reference</label>
                      <input
                        type="text"
                        value={r.reference}
                        onChange={(e) => updateRow(r.child_id, { reference: e.target.value })}
                        placeholder="e.g. bank ref / cheque no"
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                        data-testid={`wf-per-child-deposit-reference-${r.child_id}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Note (optional)</label>
                      <input
                        type="text"
                        value={r.note}
                        onChange={(e) => updateRow(r.child_id, { note: e.target.value })}
                        placeholder="Anything the team should know"
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                        data-testid={`wf-per-child-deposit-note-${r.child_id}`}
                      />
                    </div>
                    {partial && (
                      <div className="col-span-2 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5">
                        ⚠ Partial: {money(Number(r.amount))} of {money(r.required_amount)}. Balance: <b>{money(r.required_amount - Number(r.amount))}</b>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div className="pt-2 border-t border-slate-200 text-xs text-slate-600 flex items-center justify-between">
            <span>
              {anyRequired
                ? (allCaptured ? '✅ All required deposits captured' : '⏳ Some deposits still awaiting')
                : '✅ No deposits required'}
            </span>
            <span>
              {anyRequired && <>Required: <b>{money(derivedState.deposit_required_total)}</b> · Captured: <b className={allCaptured ? 'text-emerald-700' : 'text-amber-700'}>{money(derivedState.deposit_captured_total)}</b></>}
            </span>
          </div>
        </div>
      )}

      {err && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-per-child-deposit-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-per-child-deposit-cancel">Cancel</button>
          )}
          <button
            onClick={advance}
            disabled={saving}
            className="px-4 py-1.5 rounded-md text-sm bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-per-child-deposit-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : (anyRequired && !allCaptured ? 'Save & Continue (partial)' : 'Save & Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
